import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { auth } from '../utils/auth';
import { timeTrackingApi } from '../services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inactivity timeout (2 minutes in ms)
const INACTIVITY_TIMEOUT = 2 * 60 * 1000;
// How often to save to backend (30 seconds)
const SAVE_INTERVAL = 30 * 1000;
// Activity events to listen for
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

interface TimeTrackingContextType {
  isTracking: boolean;
  isPaused: boolean;
  currentCaseId: string | null;
  currentCaseName: string | null;
  currentContext: string;
  elapsedSeconds: number;
  todayTotalSeconds: number;
  sessionStartTime: number | null;
  startCaseTracking: (caseId: string, caseName: string) => void;
  startContextTracking: (context: string) => void;
  stopTracking: () => Promise<void>;
  togglePause: () => void;
  formatTime: (totalSeconds: number) => string;
  formattedElapsed: string;
  formattedTodayTotal: string;
}

interface TimeTrackingProviderProps {
  children: ReactNode;
}

/**
 * TIME TRACKING CONTEXT
 * 
 * Provides automatic time tracking across the application:
 * - Tracks time per case when in AI Workspace
 * - Tracks general work time when not on a specific case
 * - Pauses after 2 minutes of inactivity
 * - Resumes when activity is detected
 * - Periodically saves to backend
 */

const TimeTrackingContext = createContext<TimeTrackingContextType | null>(null);

export const useTimeTracking = (): TimeTrackingContextType => {
  const context = useContext(TimeTrackingContext);
  if (!context) {
    throw new Error('useTimeTracking must be used within TimeTrackingProvider');
  }
  return context;
};

export const TimeTrackingProvider: React.FC<TimeTrackingProviderProps> = ({ children }) => {
  // Current tracking state
  const [isTracking, setIsTracking] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [currentCaseId, setCurrentCaseId] = useState<string | null>(null);
  const [currentCaseName, setCurrentCaseName] = useState<string | null>(null);
  const [currentContext, setCurrentContext] = useState<string>('general'); // ai_workspace, inbox, dashboard, general
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [todayTotalSeconds, setTodayTotalSeconds] = useState<number>(0);
  
  // Refs for intervals and timeouts
  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const saveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(0);
  const sessionIdRef = useRef<string | null>(null);
  const pendingSecondsRef = useRef<number>(0);
  
  // Initialize lastActivityRef on mount
  useEffect(() => {
    lastActivityRef.current = Date.now();
  }, []);
  
  // Reset inactivity timeout
  const resetInactivityTimeout = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // If was paused, resume
    if (isPaused && isTracking) {
      setIsPaused(false);
      console.log('⏱️ Time tracking resumed due to activity');
    }
    
    // Clear existing timeout
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    
    // Set new timeout
    inactivityTimeoutRef.current = setTimeout(() => {
      if (isTracking && !isPaused) {
        setIsPaused(true);
        console.log('⏸️ Time tracking paused due to inactivity');
      }
    }, INACTIVITY_TIMEOUT);
  }, [isPaused, isTracking]);
  
  // Activity event handler
  const handleActivity = useCallback(() => {
    resetInactivityTimeout();
  }, [resetInactivityTimeout]);
  
  // Set up activity listeners
  useEffect(() => {
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [handleActivity]);
  
  // Tick interval - increment elapsed time every second when tracking and not paused
  useEffect(() => {
    if (isTracking && !isPaused) {
      tickIntervalRef.current = setInterval(() => {
        setElapsedSeconds(prev => {
          pendingSecondsRef.current += 1;
          return prev + 1;
        });
        setTodayTotalSeconds(prev => prev + 1);
      }, 1000);
    } else {
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
      }
    }
    
    return () => {
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
      }
    };
  }, [isTracking, isPaused]);
  
  // Save to backend periodically
  const saveTimeEntry = useCallback(async (finalSave: boolean = false): Promise<void> => {
    const secondsToSave = pendingSecondsRef.current;
    if (secondsToSave <= 0 && !finalSave) return;
    
    try {
      const token = auth.getToken();
      if (!token) return;
      
      await timeTrackingApi.recordTime({
        session_id: sessionIdRef.current || '',
        case_id: currentCaseId || undefined,
        context: currentContext,
        seconds: secondsToSave,
        is_final: finalSave
      });
      
      pendingSecondsRef.current = 0;
      console.log(`💾 Saved ${secondsToSave}s to ${currentCaseId || currentContext}`);
      
    } catch (error) {
      console.error('Failed to save time entry:', error);
    }
  }, [currentCaseId, currentContext]);
  
  // Set up save interval
  useEffect(() => {
    if (isTracking) {
      saveIntervalRef.current = setInterval(() => {
        saveTimeEntry(false);
      }, SAVE_INTERVAL);
    }
    
    return () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }
    };
  }, [isTracking, saveTimeEntry]);
  
  // Start tracking for a specific case
  const startCaseTracking = useCallback((caseId: string, caseName: string): void => {
    console.log(`⏱️ startCaseTracking called with: ${caseName} (id: ${caseId})`);
    
    // Save any pending time from previous session
    if (isTracking && pendingSecondsRef.current > 0) {
      saveTimeEntry(true);
    }
    
    // Generate new session ID
    sessionIdRef.current = `session-${Date.now()}`;
    
    setCurrentCaseId(caseId);
    setCurrentCaseName(caseName);
    setCurrentContext('case_detail');
    setElapsedSeconds(0);
    setSessionStartTime(Date.now());
    setIsTracking(true);
    setIsPaused(false);
    pendingSecondsRef.current = 0;
    
    resetInactivityTimeout();
    
    console.log(`⏱️ Time tracking now active for case: ${caseName}`);
  }, [isTracking, saveTimeEntry, resetInactivityTimeout]);
  
  // Start tracking for a context (not case-specific)
  const startContextTracking = useCallback((context: string): void => {
    // Save any pending time from previous session
    if (isTracking && pendingSecondsRef.current > 0) {
      saveTimeEntry(true);
    }
    
    // Generate new session ID
    sessionIdRef.current = `session-${Date.now()}`;
    
    setCurrentCaseId(null);
    setCurrentCaseName(null);
    setCurrentContext(context);
    setElapsedSeconds(0);
    setSessionStartTime(Date.now());
    setIsTracking(true);
    setIsPaused(false);
    pendingSecondsRef.current = 0;
    
    resetInactivityTimeout();
    
    console.log(`⏱️ Started tracking context: ${context}`);
  }, [isTracking, saveTimeEntry, resetInactivityTimeout]);
  
  // Stop tracking
  const stopTracking = useCallback(async () => {
    if (isTracking) {
      await saveTimeEntry(true);
    }
    
    setIsTracking(false);
    setIsPaused(false);
    setCurrentCaseId(null);
    setCurrentCaseName(null);
    setCurrentContext('general');
    setElapsedSeconds(0);
    setSessionStartTime(null);
    
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    
    console.log('⏹️ Stopped time tracking');
  }, [isTracking, saveTimeEntry]);
  
  // Manual pause/resume
  const togglePause = useCallback(() => {
    setIsPaused(prev => !prev);
    if (isPaused) {
      resetInactivityTimeout();
    }
  }, [isPaused, resetInactivityTimeout]);
  
  // Fetch today's total on mount
  useEffect(() => {
    const fetchTodayTotal = async () => {
      try {
        const token = auth.getToken();
        if (!token) return;
        
        const data = await timeTrackingApi.getToday();
        setTodayTotalSeconds(data?.total_seconds || 0);
      } catch (error) {
        console.error('Failed to fetch today total:', error);
      }
    };
    
    fetchTodayTotal();
  }, []);
  
  // Save on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isTracking && pendingSecondsRef.current > 0) {
        // Use sendBeacon for reliable delivery on page close
        const token = auth.getToken();
        if (token) {
          navigator.sendBeacon(
            `${API}/time-tracking/record`,
            JSON.stringify({
              session_id: sessionIdRef.current,
              case_id: currentCaseId,
              context: currentContext,
              seconds: pendingSecondsRef.current,
              is_final: true,
              token: token
            })
          );
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isTracking, currentCaseId, currentContext]);
  
  // Format time helper
  const formatTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  
  const value = {
    // State
    isTracking,
    isPaused,
    currentCaseId,
    currentCaseName,
    currentContext,
    elapsedSeconds,
    todayTotalSeconds,
    sessionStartTime,
    
    // Actions
    startCaseTracking,
    startContextTracking,
    stopTracking,
    togglePause,
    
    // Helpers
    formatTime,
    formattedElapsed: formatTime(elapsedSeconds),
    formattedTodayTotal: formatTime(todayTotalSeconds)
  };
  
  return (
    <TimeTrackingContext.Provider value={value}>
      {children}
    </TimeTrackingContext.Provider>
  );
};

export default TimeTrackingContext;
